import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, Send, Settings, RefreshCw, Wifi, WifiOff, Loader2, AlertCircle, Zap } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

export function WhatsAppTab({ status, qrCode, connecting, flowSettings, testPhone, 
                       onConnect, onDisconnect, onTestPhoneChange, onSendTest, 
                       onSaveFlowSettings, onRefreshStatus }) {
  const [localFlowSettings, setLocalFlowSettings] = useState(flowSettings);
  const [instanceConfig, setInstanceConfig] = useState({
    instance_name: '',
    instance_token: ''
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const API_URL = getBackendUrl();
  
  React.useEffect(() => {
    if (flowSettings) {
      setLocalFlowSettings(flowSettings);
    }
  }, [flowSettings]);
  
  React.useEffect(() => {
    const loadInstanceConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/prospection/whatsapp/instance-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setInstanceConfig(data);
        }
      } catch (err) {
        console.error('Erro ao carregar config:', err);
      }
    };
    loadInstanceConfig();
  }, [API_URL]);
  
  const handleSettingChange = (key, value) => {
    setLocalFlowSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSaveInstanceConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/prospection/whatsapp/instance-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(instanceConfig)
      });
      
      if (res.ok) {
        const result = await res.json();
        const statusEmoji = result.connected ? '✅' : '⚠️';
        alert(`${statusEmoji} ${result.message}`);
        onRefreshStatus();
      } else {
        const err = await res.json();
        alert(err.detail || 'Erro ao salvar');
      }
    } catch (err) {
      alert('Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };
  
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Instruções */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4">
        <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2 text-sm sm:text-base">
          <AlertCircle size={18} />
          Como configurar:
        </h3>
        <ol className="text-gray-300 text-xs sm:text-sm space-y-1 list-decimal list-inside">
          <li>Crie uma instância no <a href="https://uazapi.dev" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">uazapi.dev</a></li>
          <li>No painel UazAPI, escaneie o QR Code com o WhatsApp</li>
          <li>Copie o Nome da Instância e Token</li>
          <li>Cole os dados abaixo e salve</li>
        </ol>
        <div className="mt-2 p-2 bg-gray-800 rounded text-xs overflow-x-auto">
          <span className="text-gray-400">Webhook:</span> <code className="text-green-400">{API_URL}/api/prospection/webhook/incoming</code>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Configuração Manual */}
        <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700/50">
          <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Settings className="text-purple-400" size={18} />
            Configuração da Instância
          </h3>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs sm:text-sm text-gray-400 mb-1 block">Nome da Instância</label>
              <input
                type="text"
                value={instanceConfig.instance_name}
                onChange={(e) => setInstanceConfig(prev => ({ ...prev, instance_name: e.target.value }))}
                placeholder="minha-instancia"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500"
              />
            </div>
            
            <div>
              <label className="text-xs sm:text-sm text-gray-400 mb-1 block">Token da Instância</label>
              <input
                type="password"
                value={instanceConfig.instance_token}
                onChange={(e) => setInstanceConfig(prev => ({ ...prev, instance_token: e.target.value }))}
                placeholder="Cole o token aqui"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500"
              />
            </div>
            
            <button
              onClick={handleSaveInstanceConfig}
              disabled={savingConfig || !instanceConfig.instance_name || !instanceConfig.instance_token}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
            >
              {savingConfig ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Salvar Configuração
            </button>
          </div>
        </div>
        
        {/* Status */}
        <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
              {status?.connected ? (
                <Wifi className="text-green-400" size={18} />
              ) : (
                <WifiOff className="text-yellow-400" size={18} />
              )}
              Status
            </h3>
            <button
              onClick={onRefreshStatus}
              className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          
          <div className={`p-3 rounded-xl ${
            status?.connected 
              ? 'bg-green-500/20 border border-green-500/30' 
              : status?.status === 'qrcode'
                ? 'bg-blue-500/20 border border-blue-500/30'
                : 'bg-yellow-500/20 border border-yellow-500/30'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                status?.connected ? 'bg-green-500' : status?.status === 'qrcode' ? 'bg-blue-500 animate-pulse' : 'bg-yellow-500'
              }`}>
                {status?.connected ? (
                  <CheckCircle size={20} className="text-white" />
                ) : (
                  <AlertCircle size={20} className="text-white" />
                )}
              </div>
              <div className="min-w-0">
                <p className={`font-semibold text-sm ${
                  status?.connected ? 'text-green-400' : status?.status === 'qrcode' ? 'text-blue-400' : 'text-yellow-400'
                }`}>
                  {status?.connected ? 'Conectado' : status?.status === 'qrcode' ? 'Aguardando QR Code' : status?.message || 'Desconectado'}
                </p>
                {status?.phone_number && (
                  <p className="text-gray-300 text-xs truncate">{status.phone_number}</p>
                )}
              </div>
            </div>
          </div>
          
          {status?.connected && (
            <div className="mt-3 space-y-2">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => onTestPhoneChange(e.target.value)}
                placeholder="5511999999999"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
              />
              <button
                onClick={onSendTest}
                disabled={!testPhone}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg text-sm flex items-center justify-center gap-2"
              >
                <Send size={14} />
                Enviar Teste
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Controle de Fluxo */}
      <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
            <Zap className="text-yellow-400" size={18} />
            Controle de Fluxo
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-gray-400 text-sm">Ativo</span>
            <div 
              onClick={() => handleSettingChange('enabled', !localFlowSettings?.enabled)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                localFlowSettings?.enabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                localFlowSettings?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </div>
          </label>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Limite/dia</label>
            <input
              type="number"
              value={localFlowSettings?.daily_limit || 100}
              onChange={(e) => handleSettingChange('daily_limit', parseInt(e.target.value))}
              className="w-full px-2 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Min (s)</label>
            <input
              type="number"
              value={localFlowSettings?.min_interval || 30}
              onChange={(e) => handleSettingChange('min_interval', parseInt(e.target.value))}
              className="w-full px-2 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Máx (s)</label>
            <input
              type="number"
              value={localFlowSettings?.max_interval || 60}
              onChange={(e) => handleSettingChange('max_interval', parseInt(e.target.value))}
              className="w-full px-2 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">IA</label>
            <select
              value={localFlowSettings?.ai_model || 'gemini'}
              onChange={(e) => handleSettingChange('ai_model', e.target.value)}
              className="w-full px-2 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value="gemini">Gemini</option>
              <option value="openai">GPT</option>
            </select>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFlowSettings?.ai_enabled || false}
              onChange={(e) => handleSettingChange('ai_enabled', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-gray-300 text-xs sm:text-sm">IA Ativa</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFlowSettings?.ai_auto_respond || false}
              onChange={(e) => handleSettingChange('ai_auto_respond', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-gray-300 text-xs sm:text-sm">Auto-resposta</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localFlowSettings?.ai_classify_responses || false}
              onChange={(e) => handleSettingChange('ai_classify_responses', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-gray-300 text-xs sm:text-sm">Classificar</span>
          </label>
        </div>
        
        <button
          onClick={() => onSaveFlowSettings(localFlowSettings)}
          className="w-full sm:w-auto px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
        >
          <Save size={16} />
          Salvar
        </button>
      </div>
    </div>
  );
}
